import * as React from 'react'

import { Avatar, Box, Text, Touchable, useToggle } from '@factorialco/deprecated-design-system'
import DOMPurify from 'dompurify'
import moment from 'moment'

import { observer } from 'mobx-react'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'
import type IncidenceNote from 'modules/bookkeepers/store/models/IncidenceNote'

import DeleteModal from './_DeleteModal'

interface Props {
  note: IncidenceNote
}

const getAuthor = (note: IncidenceNote) => {
  if (note.attributes?.author_id) {
    return {
      fullName: note.attributes.author_name,
      avatarUrl: note.attributes.author_avatar,
    }
  } else {
    return {
      fullName: i18n.t('bookkeepers.incidence.note.deleted_user'),
      avatarUrl: null,
    }
  }
}

const IncidenceNoteItem = ({ note }: Props) => {
  const author = getAuthor(note)
  const [isHovering, toggleHovering] = useToggle(false)
  const [isOpen, onToggleModal] = React.useState(false)
  const [textHovering, toggleTextHovering] = useToggle(false)
  const textColor = textHovering ? 'grey800' : 'grey600'
  const backgroundColor = isHovering ? 'grey100' : 'lighter000'
  const isBookkeeperView = useBookkeeperView()

  const { running: deleting, performAction: onDeleteNote } = useAsyncAction(
    async () => {
      const { success } = await note.destroy({
        data: {
          bookkeeper_view: isBookkeeperView,
        },
      })

      if (success) onToggleModal(false)
    },
    {
      successFlash: true,
      errorFlash: true,
    }
  )

  return (
    <>
      {isOpen && (
        <DeleteModal
          deleting={deleting}
          onDeleteNote={onDeleteNote}
          onToggleModal={onToggleModal}
        />
      )}
      <Box
        gap="s12"
        width="full"
        paddingY="s8"
        flexDirection="row"
        background={backgroundColor}
        onMouseEnter={toggleHovering(true)}
        onMouseLeave={toggleHovering(false)}
      >
        <Box>
          <Avatar url={buildUrl(author.avatarUrl)} label={author.fullName} />
        </Box>
        <Box width="full" gap="s2">
          <Box flexDirection="row" gap="s8">
            <Box>
              <Text.Normal size="200" weight="semibold" color="grey1000">
                {author.fullName}
              </Text.Normal>
            </Box>
            <Box alignSelf="center">
              <Text.Normal size="100" weight="normal" color="grey600">
                {moment(note.attributes.created_at).format('hh:mm a')}
              </Text.Normal>
            </Box>
            {(isHovering || isOpen) && (
              <Box
                paddingTop="s2"
                paddingLeft="s120"
                onMouseEnter={toggleTextHovering(true)}
                onMouseLeave={toggleTextHovering(false)}
              >
                <Touchable onClick={() => onToggleModal(true)}>
                  <Text size="100" underlined htmlTag="div" weight="medium" color={textColor}>
                    {i18n.t('bookkeepers.incidence.note.remove_link')}
                  </Text>
                </Touchable>
              </Box>
            )}
          </Box>
          <Text.Normal weight="normal" color="grey1000">
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(note.attributes.content),
              }}
            />
          </Text.Normal>
        </Box>
      </Box>
    </>
  )
}

export default observer(IncidenceNoteItem)
